import policies from "../server/config/policies.json";

const SITE_URL = "https://georgiadream.ge";
const COORDS = { lat: 41.7228, lng: 41.7282 } as const;

type ImageRef = { url?: string };

interface ApartmentFrontmatter {
  title: string;
  listing_id: number | string;
  hotel?: string;
  description?: string;
  floor?: number | string;
  rooms?: number | string;
  capacity?: number | string;
  area_sqm?: number;
  photo_gallery?: ImageRef[];
  amenities?: {
    bedroom?: string[];
    kitchen?: string[];
    bath?: string[];
    comfort?: string[];
    outdoor?: string[];
    extras?: string[];
  };
}

export interface ApartmentPriceRange {
  lowPrice: number;
  highPrice: number;
  offerCount: number;
}

export interface ApartmentSeasonOffer {
  name: string;
  dateStart: string;
  dateEnd: string;
  dailyRateUsd: number;
}

export interface ApartmentLdInput {
  apartment: ApartmentFrontmatter;
  canonicalUrl: string;
  locale: string;
  fromNightly: number | null;
  priceRange?: ApartmentPriceRange | null;
  // Current/upcoming seasons from /api/apartments/seasons — emitted as dated
  // Offers so AI agents can answer "how much on <date>" without a quote call.
  seasons?: ApartmentSeasonOffer[] | null;
}

const toAbsUrl = (path: string) =>
  path.startsWith("http") ? path : `${SITE_URL}${path}`;

function flattenAmenities(amenities: ApartmentFrontmatter["amenities"]): string[] {
  if (!amenities) return [];
  return [
    ...(amenities.bedroom ?? []),
    ...(amenities.kitchen ?? []),
    ...(amenities.bath ?? []),
    ...(amenities.comfort ?? []),
    ...(amenities.outdoor ?? []),
    ...(amenities.extras ?? []),
  ];
}

export function buildApartmentLd({
  apartment: a,
  canonicalUrl,
  locale,
  fromNightly,
  priceRange,
  seasons,
}: ApartmentLdInput) {
  const images = Array.isArray(a.photo_gallery)
    ? a.photo_gallery
        .map((p) => p?.url)
        .filter((u): u is string => typeof u === "string" && u.length > 0)
        .map(toAbsUrl)
    : [];

  const amenityList = flattenAmenities(a.amenities);

  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Apartment",
    "@id": `${canonicalUrl}#apartment`,
    name: a.title,
    url: canonicalUrl,
    description: a.description,
    inLanguage: locale,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Batumi st. 16",
      addressLocality: "Chakvi",
      addressRegion: "Adjara",
      postalCode: "6200",
      addressCountry: "GE",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: COORDS.lat,
      longitude: COORDS.lng,
    },
    // Apartment inherits review signals from the parent LodgingBusiness
    // via this @id cross-graph reference — Google resolves the rating from
    // /#lodgingbusiness instead of repeating the same 54 reviews on every
    // unit (which Google penalises as inflated/duplicate review markup).
    containedInPlace: {
      "@type": "LodgingBusiness",
      "@id": `${SITE_URL}/#lodgingbusiness`,
      name: a.hotel || "Dreamland Oasis Chakvi",
    },
    // Booking conditions — single source: server/config/policies.json.
    checkinTime: policies.checkinTime,
    checkoutTime: policies.checkoutTime,
    petsAllowed: policies.petsAllowed,
    smokingAllowed: policies.smokingAllowed,
    // Lets agents construct a dated deep link straight into the booking flow.
    potentialAction: {
      "@type": "ReserveAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${canonicalUrl}?from={checkin}&to={checkout}&guests={guests}`,
        actionPlatform: [
          "https://schema.org/DesktopWebPlatform",
          "https://schema.org/MobileWebPlatform",
        ],
      },
      result: { "@type": "LodgingReservation", name: a.title },
    },
  };

  if (images.length) ld.image = images;
  if (a.rooms) ld.numberOfRooms = Number(a.rooms);
  if (a.floor) ld.floorLevel = String(a.floor);
  if (typeof a.area_sqm === "number" && a.area_sqm > 0) {
    ld.floorSize = {
      "@type": "QuantitativeValue",
      value: a.area_sqm,
      unitCode: "MTK",
    };
  }
  if (a.capacity) {
    ld.occupancy = {
      "@type": "QuantitativeValue",
      maxValue: Number(a.capacity),
      unitText: "person",
    };
  }
  if (amenityList.length) {
    ld.amenityFeature = amenityList.map((name) => ({
      "@type": "LocationFeatureSpecification",
      name,
      value: true,
    }));
  }
  const seasonOffers = (seasons ?? [])
    .filter((s) => s.dailyRateUsd > 0)
    .map((s) => ({
      "@type": "Offer",
      name: s.name,
      price: s.dailyRateUsd,
      priceCurrency: policies.currency,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: s.dailyRateUsd,
        priceCurrency: policies.currency,
        unitCode: "DAY",
        referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "DAY" },
      },
      validFrom: s.dateStart,
      validThrough: s.dateEnd,
      availability: "https://schema.org/InStock",
      url: canonicalUrl,
    }));
  if (priceRange && priceRange.lowPrice > 0 && priceRange.highPrice > 0) {
    const aggregate = {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: priceRange.lowPrice,
      highPrice: priceRange.highPrice,
      offerCount: priceRange.offerCount,
      availability: "https://schema.org/InStock",
      url: canonicalUrl,
    };
    // AggregateOffer first (Rich Results), dated season Offers after (agents).
    ld.offers = seasonOffers.length ? [aggregate, ...seasonOffers] : aggregate;
  } else if (seasonOffers.length) {
    ld.offers = seasonOffers;
  } else if (Number.isFinite(fromNightly ?? NaN) && (fromNightly ?? 0) > 0) {
    ld.offers = {
      "@type": "Offer",
      price: fromNightly,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: canonicalUrl,
    };
  }

  return ld;
}

export interface BreadcrumbLdInput {
  listingName: string;
  canonicalUrl: string;
  homeUrl: string;
  labels: { home: string };
}

// Two-level breadcrumb (Home → Apartment). The former middle item pointed at
// `/#apartments` — a hash anchor on the homepage, which Google cannot crawl as
// a distinct page and which can invalidate the whole BreadcrumbList. When a real
// /apartments hub page exists (see growth roadmap), re-introduce it here as a
// crawlable position-2 item and bump the listing to position 3.
export function buildBreadcrumbLd({
  listingName,
  canonicalUrl,
  homeUrl,
  labels,
}: BreadcrumbLdInput) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: labels.home,
        item: homeUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: listingName,
        item: canonicalUrl,
      },
    ],
  };
}
